/**
 * SetupWizard — fires only on a brand-new CourseForge clone whose users
 * collection is empty. Four steps: institution + course + brand + admin.
 * Posts to /api/setup/initialize which is race-safe (409 if users exist).
 *
 * On success: logs in the new institution_admin and routes to
 * /institution-admin.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Step = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2 text-xs" data-testid={`setup-step-${n}`}>
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center font-bold"
      style={{
        background: done ? '#16a34a' : active ? 'var(--unthsc-green)' : '#e5e7eb',
        color: done || active ? 'white' : '#6b7280',
      }}
    >
      {done ? '✓' : n}
    </div>
    <span className={active ? 'font-semibold text-gray-900' : 'text-gray-500'}>
      {label}
    </span>
  </div>
);

const SetupWizard = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [data, setData] = useState({
    institution_name: '',
    course_title: '',
    course_subtitle: '',
    primary_color: '#00853E',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    admin_password_confirm: '',
  });

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const canNext1 = data.institution_name.trim().length > 0;
  const canNext2 = data.course_title.trim().length > 0;
  const canNext3 = /^#[0-9a-fA-F]{6}$/.test(data.primary_color);
  const canSubmit =
    data.admin_name.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.admin_email) &&
    data.admin_password.length >= 8 &&
    data.admin_password === data.admin_password_confirm;

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/setup/initialize`, {
        institution_name: data.institution_name.trim(),
        course_title: data.course_title.trim(),
        course_subtitle: data.course_subtitle.trim(),
        primary_color: data.primary_color,
        admin_name: data.admin_name.trim(),
        admin_email: data.admin_email.trim(),
        admin_password: data.admin_password,
      });
      localStorage.setItem('token', res.data.token);
      await checkAuth();
      navigate('/institution-admin', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Setup failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50" data-testid="setup-wizard">
      <div className="w-full max-w-2xl card p-8">
        <h1 className="text-3xl font-bold" style={{ color: 'var(--unthsc-green)' }}>
          Welcome to CourseForge
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          This is a fresh deployment. Let's get your institution set up — 4 quick steps.
        </p>

        <div className="flex items-center gap-3 mb-8" data-testid="setup-steps-bar">
          <Step n={1} label="Institution" active={step === 1} done={step > 1} />
          <div className="flex-1 h-px bg-gray-200" />
          <Step n={2} label="Course" active={step === 2} done={step > 2} />
          <div className="flex-1 h-px bg-gray-200" />
          <Step n={3} label="Brand" active={step === 3} done={step > 3} />
          <div className="flex-1 h-px bg-gray-200" />
          <Step n={4} label="Admin" active={step === 4} done={false} />
        </div>

        {step === 1 && (
          <div data-testid="setup-step-1-panel">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Institution name</label>
            <input
              type="text"
              value={data.institution_name}
              onChange={set('institution_name')}
              placeholder="e.g., Harvard Law School"
              className="input-field mb-4"
              data-testid="setup-institution-input"
              autoFocus
            />
            <p className="text-xs text-gray-500 mb-6">
              Shown in the login screen, navbar, and emails. You can change it later in
              <code className="bg-gray-100 px-1 mx-1 rounded">backend/.env</code>.
            </p>
            <div className="flex justify-end">
              <button onClick={() => setStep(2)} disabled={!canNext1} className="btn-primary" data-testid="setup-next-1">
                Next →
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="setup-step-2-panel">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Course title</label>
            <input
              type="text"
              value={data.course_title}
              onChange={set('course_title')}
              placeholder="e.g., Constitutional Law AI Tutor"
              className="input-field mb-4"
              data-testid="setup-course-title-input"
            />
            <label className="block text-sm font-semibold text-gray-700 mb-1">Course subtitle (optional)</label>
            <input
              type="text"
              value={data.course_subtitle}
              onChange={set('course_subtitle')}
              placeholder="e.g., Case-Based Reasoning with AI"
              className="input-field mb-6"
              data-testid="setup-course-subtitle-input"
            />
            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="btn-outline" data-testid="setup-back-2">← Back</button>
              <button onClick={() => setStep(3)} disabled={!canNext2} className="btn-primary" data-testid="setup-next-2">Next →</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="setup-step-3-panel">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Primary brand color</label>
            <div className="flex items-center gap-3 mb-2">
              <input
                type="color"
                value={data.primary_color}
                onChange={set('primary_color')}
                className="w-14 h-10 rounded border border-gray-300 cursor-pointer"
                data-testid="setup-color-picker"
              />
              <input
                type="text"
                value={data.primary_color}
                onChange={set('primary_color')}
                placeholder="#00853E"
                className="input-field flex-1 font-mono"
                data-testid="setup-color-hex-input"
              />
            </div>
            <p className="text-xs text-gray-500 mb-6">
              Hex code (e.g., <code className="bg-gray-100 px-1 rounded">#9F1239</code>). Used for buttons and headers.
            </p>
            <div className="p-4 rounded mb-6" style={{ background: data.primary_color, color: 'white' }} data-testid="setup-color-preview">
              <div className="text-xs uppercase tracking-wider opacity-80">Preview</div>
              <div className="text-lg font-bold">{data.course_title || 'Your course title here'}</div>
            </div>
            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="btn-outline" data-testid="setup-back-3">← Back</button>
              <button onClick={() => setStep(4)} disabled={!canNext3} className="btn-primary" data-testid="setup-next-3">Next →</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="setup-step-4-panel">
            <p className="text-sm text-gray-600 mb-4">
              You will be the <span className="font-semibold">first institution admin</span> for this deployment.
            </p>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Your name</label>
            <input
              type="text"
              value={data.admin_name}
              onChange={set('admin_name')}
              className="input-field mb-4"
              data-testid="setup-admin-name-input"
            />
            <label className="block text-sm font-semibold text-gray-700 mb-1">Your email</label>
            <input
              type="email"
              value={data.admin_email}
              onChange={set('admin_email')}
              className="input-field mb-4"
              data-testid="setup-admin-email-input"
            />
            <label className="block text-sm font-semibold text-gray-700 mb-1">Password (min 8 chars)</label>
            <input
              type="password"
              value={data.admin_password}
              onChange={set('admin_password')}
              className="input-field mb-4"
              data-testid="setup-admin-password-input"
            />
            <label className="block text-sm font-semibold text-gray-700 mb-1">Confirm password</label>
            <input
              type="password"
              value={data.admin_password_confirm}
              onChange={set('admin_password_confirm')}
              className="input-field mb-4"
              data-testid="setup-admin-password-confirm"
            />
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-testid="setup-error">
                {error}
              </div>
            )}
            <div className="flex justify-between">
              <button onClick={() => setStep(3)} className="btn-outline" data-testid="setup-back-4">← Back</button>
              <button
                onClick={submit}
                disabled={!canSubmit || submitting}
                className="btn-primary"
                data-testid="setup-submit-btn"
              >
                {submitting ? 'Creating…' : '✓ Complete Setup'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SetupWizard;
